import es from 'date-fns/locale/es';
import moment from 'moment';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { default as React, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as PROFILE from "../../../constants/profile";
import { DRIVER_REPORTS } from '../../../constants/reports';
import { useAuth } from '../../../hooks/useAuth';

registerLocale('es', es)

function DriverReport() {

  // #region vars and consts
  
  const { hasPermission, user } = useAuth();
  const [dateType, setDateType] = useState(DRIVER_REPORTS.DAY);
  const [report, setReport] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'orderId': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'orderNo': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'departureTime': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'departureTimeT': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'driverComment': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'price': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  });

  // #endregion

  // #region methods

  const getDriverReport = (
    userId,
    dateType
  ) => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/order/reportOfDriver`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        dateType
      })
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        const _report = data.responseObject;        
        setReport(_report);
      }
      setLoading(false);
    });
  }

  const scrollToTop = () => { 
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);    
  }

  // #endregion

  // #region events

  const onDateTypeChange = (e) => {
    setDateType(e.value)
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilter(value);
  }

  const onSearchButtonPress = () => {
    setReport([]);
    getDriverReport(
      parseInt(user.userId),
      dateType
    );
  }

  useEffect(() => {
    scrollToTop();
  }, []);

  // #endregion

  // #region templates

  const filtersTemplate = (
    <>
      <Row className='pb-2'>
        <Col md={12}>
          <div className="flex flex-wrap gap-3" style={{
            display: "flex"
          }}>
            <div className="flex align-items-center">
              <RadioButton 
                inputId="dateTypesDayRadioButton" 
                name="dateTypesRadioButtons" 
                value={DRIVER_REPORTS.DAY} 
                onChange={onDateTypeChange} 
                checked={dateType === DRIVER_REPORTS.DAY} 
              />
              <label 
                htmlFor="dateTypesDayRadioButton" 
                className="ml-2">
                  Día actual
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton 
                inputId="dateTypesWeekRadioButton" 
                name="dateTypesRadioButtons" 
                value={DRIVER_REPORTS.WEEK} 
                onChange={onDateTypeChange} 
                checked={dateType === DRIVER_REPORTS.WEEK} 
              />
              <label 
                htmlFor="dateTypesWeekRadioButton" 
                className="ml-2">
                  Semana actual
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton 
                inputId="dateTypesLastWeekRadioButton" 
                name="dateTypesRadioButtons" 
                value={DRIVER_REPORTS.LAST_WEEK} 
                onChange={onDateTypeChange} 
                checked={dateType === DRIVER_REPORTS.LAST_WEEK} 
              />
              <label 
                htmlFor="dateTypesLastWeekRadioButton" 
                className="ml-2">
                  Semana pasada
              </label>
            </div>
          </div>         
        </Col>    
      </Row>      
      <Row>
        <Col>
          <Button
            type="button"
            icon="pi pi-search"
            onClick={onSearchButtonPress}
            className="p-button-raised p-button-sm p-button-primary"
            label='Buscar' />
        </Col>
      </Row>
    </>
  );

  const reportHeaderTemplate = (
    <>
      <Row>
        <Col>
          <span className="p-input-icon-left">
            <i className="pi pi-filter" />
            <InputText value={globalFilter} onChange={onGlobalFilterChange} placeholder="Filtrar" className='p-inputtext-sm' />
          </span>
        </Col>
      </Row>
    </>
  );

  const departureTimeTTemplate = (row) => {
    const departureTimeT = row.departureTimeT;
    if (departureTimeT > 0) {
      const departureTimeTMoment = moment(departureTimeT);
      return (
        <span>
          {departureTimeTMoment.format("DD/MM/YYYY hh:mm:ss a")}
        </span>
      );
    } else {
      return <></>;
    }
  }

  const resultTemplate = (
    <>
      <div>
        <div className="card">
          <DataTable
            value={report}
            paginator
            className="p-datatable-customers"
            showGridlines
            stripedRows
            header={reportHeaderTemplate}
            rows={50}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rowsPerPageOptions={[10, 25, 50]}
            filters={filters} filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            globalFilterFields={[              
              'orderNo',
              'departureTime',              
              'driverComment',
              'price',
            ]}
            emptyMessage="No records found."
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
            <Column field="orderNo" header="No. Orden" sortable filter filterPlaceholder="Buscar por No. Orden" className='app-w-150-px text-end' />
            <Column field="departureTimeT" header="Fecha de salida" sortable filter filterPlaceholder="Buscar por Fecha de salida" className='app-w-300-px text-start' body={departureTimeTTemplate} />
            <Column field="driverComment" header="Comentarios del conductor" sortable filter filterPlaceholder="Buscar por Comentarios del conductor" className='app-w-500-px text-start' />
            <Column field="price" header="Precio" sortable filter filterPlaceholder="Buscar por Precio" className='app-w-150-px text-end' />
          </DataTable>
        </div>
      </div>
    </>
  );

  const reportTemplate = (
    <>
      <article>
        <Row>
          <Col>
            <h1 className='app-font'>
              Reporte de Conductor
            </h1>
          </Col>
        </Row>
        <Row>
          <Col>
            {filtersTemplate}
          </Col>
        </Row>
        <Row className='pt-2'>
          <Col>
            {resultTemplate}
          </Col>
        </Row>
      </article>
    </>
  );

  // #endregion

  return (
    <>
      {reportTemplate}
    </>
  );
}

export default DriverReport;